import { type ClobTradeRow } from '@cpc/tx';
import { useEffect, useRef, useState } from 'react';

import { getPushHubHttpUrl } from '~/config/clobConfig';
import {
	applyPushedOrder,
	channelsFromHealth,
	fetchPushHubHealth,
	fetchPushHubTrades,
	mergeTrades,
	pushOrderToRow,
	pushTradeToClobRow,
	type PushHubOrderRow,
	type PushOrderWire,
	type PushTradeWire,
} from '~/hooks/pushHubClient';

/** 盘口价位：`dataPerDgc` = 链上主报价（每 1 DGC 的 DATA）；`dgcPerData` 为倒数仅供展示。 */
export type BookLevel = {
	priceTicks: string;
	dataPerDgc: string;
	dgcPerData: string;
	qtyLots: string;
};

export type KlineCandle = {
	interval: string;
	openTimeMs: number;
	open: number;
	high: number;
	low: number;
	close: number;
	volumeLots: string;
	closed: boolean;
};

type PushKlineRaw = {
	interval?: string;
	open_time_ms?: number;
	open_ticks?: string;
	high_ticks?: string;
	low_ticks?: string;
	close_ticks?: string;
	volume_lots?: string;
	closed?: boolean;
};

type PushEnvelope = {
	type: 'snapshot' | 'delta' | 'trade' | 'pong' | 'kline' | 'order';
	ch?: string;
	bids?: unknown;
	asks?: unknown;
	b?: unknown;
	a?: unknown;
	kline?: PushKlineRaw;
	klines?: PushKlineRaw[];
	trade?: PushTradeWire;
	order?: PushOrderWire;
	open_orders?: PushOrderWire[];
	history_orders?: PushOrderWire[];
};

/** price_ticks → DATA per DGC（数值）。函数名历史遗留，语义是 dataPerDgc。 */
export function ticksToDgcPerData(ticks: string | undefined): number {
	return Number(BigInt(ticks || '0')) / 1e9;
}

function ticksToDataPerDgcString(ticks: string | undefined): string {
	const n = ticksToDgcPerData(ticks);
	if (!Number.isFinite(n) || n <= 0) return '0';
	return n.toFixed(9).replace(/\.?0+$/, '') || '0';
}

function reciprocalPriceString(dataPerDgc: string): string {
	const n = Number.parseFloat(dataPerDgc);
	if (!Number.isFinite(n) || n <= 0) return '—';
	const inv = 1 / n;
	if (Math.abs(inv - Math.round(inv)) < 1e-9) return String(Math.round(inv));
	return inv.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
}

function parseKline(raw: PushKlineRaw): KlineCandle | null {
	if (!raw.open_time_ms) return null;
	return {
		interval: raw.interval ?? '',
		openTimeMs: raw.open_time_ms,
		open: ticksToDgcPerData(raw.open_ticks),
		high: ticksToDgcPerData(raw.high_ticks),
		low: ticksToDgcPerData(raw.low_ticks),
		close: ticksToDgcPerData(raw.close_ticks),
		volumeLots: raw.volume_lots ?? '0',
		closed: !!raw.closed,
	};
}

function mergeKline(candles: KlineCandle[], update: KlineCandle): KlineCandle[] {
	const idx = candles.findIndex((c) => c.openTimeMs === update.openTimeMs);
	if (idx >= 0) {
		const next = [...candles];
		next[idx] = update;
		return fillKlineGaps(next, update.interval || candles[0]?.interval || '1m');
	}
	return fillKlineGaps(
		[...candles, update].sort((a, b) => a.openTimeMs - b.openTimeMs),
		update.interval || candles[0]?.interval || '1m',
	);
}

const INTERVAL_MS: Record<string, number> = {
	'1m': 60_000,
	'5m': 300_000,
	'15m': 900_000,
	'1h': 3_600_000,
};

export function intervalMs(interval: string): number {
	return INTERVAL_MS[interval] ?? 60_000;
}

/** Insert flat candles for minutes with no trades (carry forward last close). */
export function fillKlineGaps(
	candles: KlineCandle[],
	interval: string,
	limit = 200,
): KlineCandle[] {
	if (candles.length === 0) return candles;
	const durMs = INTERVAL_MS[interval] ?? 60_000;
	const sorted = [...candles].sort((a, b) => a.openTimeMs - b.openTimeMs);
	const nowBucket = Math.floor(Date.now() / durMs) * durMs;
	const end = Math.max(sorted[sorted.length - 1]!.openTimeMs, nowBucket);
	let start = sorted[0]!.openTimeMs;
	const windowStart = end - (limit - 1) * durMs;
	if (windowStart > start) start = windowStart;

	const byTime = new Map(sorted.map((c) => [c.openTimeMs, c]));
	const out: KlineCandle[] = [];
	let prevClose = sorted[0]!.close;
	for (let t = start; t <= end; t += durMs) {
		const existing = byTime.get(t);
		if (existing) {
			out.push(existing);
			prevClose = existing.close;
			continue;
		}
		out.push({
			interval,
			openTimeMs: t,
			open: prevClose,
			high: prevClose,
			low: prevClose,
			close: prevClose,
			volumeLots: '0',
			closed: t < nowBucket,
		});
	}
	return out.slice(-limit);
}

function parseLevels(raw: unknown): { priceTicks: string; qtyLots: string }[] {
	if (!Array.isArray(raw)) return [];
	const out: { priceTicks: string; qtyLots: string }[] = [];
	for (const item of raw) {
		if (Array.isArray(item) && item.length >= 2) {
			out.push({ priceTicks: String(item[0]), qtyLots: String(item[1]) });
			continue;
		}
		if (!item || typeof item !== 'object') continue;
		const o = item as Record<string, string>;
		const priceTicks = String(o.priceTicks ?? o.PriceTicks ?? '').trim();
		const qtyLots = String(o.qtyLots ?? o.QtyLots ?? '').trim();
		if (priceTicks) out.push({ priceTicks, qtyLots: qtyLots || '0' });
	}
	return out;
}

function toBookLevel(l: { priceTicks: string; qtyLots: string }): BookLevel {
	const dataPerDgc = ticksToDataPerDgcString(l.priceTicks);
	return {
		priceTicks: l.priceTicks,
		dataPerDgc,
		dgcPerData: reciprocalPriceString(dataPerDgc),
		qtyLots: l.qtyLots,
	};
}

export async function fetchKlineHistory(
	interval: string,
	limit = 200,
	pair?: string,
): Promise<KlineCandle[]> {
	const params = new URLSearchParams({
		interval,
		limit: String(limit),
	});
	if (pair) params.set('pair', pair);
	const url = `${getPushHubHttpUrl()}/v1/kline?${params}`;
	const res = await fetch(url);
	if (!res.ok) throw new Error(`kline HTTP ${res.status}`);
	const body = (await res.json()) as { klines?: PushKlineRaw[] };
	return fillKlineGaps(
		(body.klines ?? [])
			.map(parseKline)
			.filter((c): c is KlineCandle => c != null),
		interval,
		limit,
	);
}

export function usePushHub(
	wsUrl: string,
	enabled: boolean,
	options?: {
		klineInterval?: string;
		depthLimit?: number;
		tradeLimit?: number;
		/** Push Hub channel pair label, e.g. DATA-CPC / CPC-DATA */
		pair?: string;
		/** Wallet address — required to receive 目前委托 / 委托历史 on the orders WS channel. */
		owner?: string;
		onTrade?: () => void;
	},
): {
	connected: boolean;
	bids: BookLevel[];
	asks: BookLevel[];
	klines: KlineCandle[];
	trades: ClobTradeRow[];
	openOrders: PushHubOrderRow[];
	historyOrders: PushHubOrderRow[];
	error?: string;
} {
	const klineInterval = options?.klineInterval ?? '1m';
	const depthLimit = options?.depthLimit ?? 20;
	const tradeLimit = options?.tradeLimit ?? 100;
	const pair = options?.pair?.trim() || undefined;
	const owner = options?.owner?.trim() || undefined;
	const onTrade = options?.onTrade;
	const onTradeRef = useRef(onTrade);
	onTradeRef.current = onTrade;

	const [connected, setConnected] = useState(false);
	const [error, setError] = useState<string>();
	const [bids, setBids] = useState<BookLevel[]>([]);
	const [asks, setAsks] = useState<BookLevel[]>([]);
	const [klines, setKlines] = useState<KlineCandle[]>([]);
	const [trades, setTrades] = useState<ClobTradeRow[]>([]);
	const [openOrders, setOpenOrders] = useState<PushHubOrderRow[]>([]);
	const [historyOrders, setHistoryOrders] = useState<PushHubOrderRow[]>([]);
	const [channels, setChannels] = useState<{
		depthCh: string;
		tradeCh: string;
		klineCh: string;
		ordersCh: string;
	} | null>(null);

	const bidMap = useRef(new Map<string, BookLevel>());
	const askMap = useRef(new Map<string, BookLevel>());
	const channelsRef = useRef(channels);
	channelsRef.current = channels;
	const wsRef = useRef<WebSocket | null>(null);
	const depthLimitRef = useRef(depthLimit);
	depthLimitRef.current = depthLimit;
	const klineIntervalRef = useRef(klineInterval);
	klineIntervalRef.current = klineInterval;
	const tradeLimitRef = useRef(tradeLimit);
	tradeLimitRef.current = tradeLimit;
	const ownerRef = useRef(owner);
	ownerRef.current = owner;
	const openOrdersRef = useRef<PushHubOrderRow[]>([]);
	const historyOrdersRef = useRef<PushHubOrderRow[]>([]);

	useEffect(() => {
		if (owner) return;
		openOrdersRef.current = [];
		historyOrdersRef.current = [];
		setOpenOrders([]);
		setHistoryOrders([]);
	}, [owner]);

	// Seed REST data whenever pair / interval changes (do not null channels mid-switch).
	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		setBids([]);
		setAsks([]);
		setKlines([]);
		setTrades([]);
		bidMap.current.clear();
		askMap.current.clear();
		Promise.all([
			fetchPushHubHealth()
				.then((h) => channelsFromHealth(h, klineInterval, pair))
				.catch(() => channelsFromHealth({}, klineInterval, pair)),
			fetchPushHubTrades(tradeLimit, pair).catch(() => [] as ClobTradeRow[]),
			fetchKlineHistory(klineInterval, 200, pair).catch(() => [] as KlineCandle[]),
		]).then(([ch, seedTrades, seedKlines]) => {
			if (cancelled) return;
			setChannels(ch);
			if (seedTrades.length) setTrades(seedTrades);
			if (seedKlines.length) setKlines(seedKlines);
		});
		return () => {
			cancelled = true;
		};
	}, [enabled, klineInterval, tradeLimit, pair]);

	useEffect(() => {
		if (!enabled) return;
		const dur = intervalMs(klineInterval);
		const id = setInterval(() => {
			setKlines((prev) => (prev.length ? fillKlineGaps(prev, klineInterval) : prev));
		}, Math.min(dur, 60_000));
		return () => clearInterval(id);
	}, [enabled, klineInterval]);

	// One long-lived WebSocket per URL; pair switch only resubscribes.
	useEffect(() => {
		if (!enabled || !wsUrl) {
			setConnected(false);
			return;
		}

		let closed = false;
		let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
		let ws: WebSocket | null = null;

		const emitDepth = () => {
			const lim = depthLimitRef.current;
			const sortBid = (a: BookLevel, b: BookLevel) =>
				BigInt(b.priceTicks) > BigInt(a.priceTicks) ? 1 : -1;
			const sortAsk = (a: BookLevel, b: BookLevel) =>
				BigInt(a.priceTicks) > BigInt(b.priceTicks) ? 1 : -1;
			setBids([...bidMap.current.values()].sort(sortBid).slice(0, lim));
			setAsks([...askMap.current.values()].sort(sortAsk).slice(0, lim));
		};

		const mergeSide = (
			map: Map<string, BookLevel>,
			levels: { priceTicks: string; qtyLots: string }[],
		) => {
			for (const l of levels) {
				if (BigInt(l.qtyLots || '0') === 0n) map.delete(l.priceTicks);
				else map.set(l.priceTicks, toBookLevel(l));
			}
		};

		const subscribe = () => {
			const ch = channelsRef.current;
			if (!ws || ws.readyState !== WebSocket.OPEN || !ch) return;
			const ownerNow = ownerRef.current;
			const channels = [ch.depthCh, ch.tradeCh, ch.klineCh];
			if (ownerNow) channels.push(ch.ordersCh);
			ws.send(
				JSON.stringify({
					op: 'subscribe',
					ch: channels,
					depth: depthLimitRef.current,
					...(ownerNow ? { owner: ownerNow } : {}),
				}),
			);
		};

		const handle = (env: PushEnvelope) => {
			const ch = channelsRef.current;
			if (!ch) return;
			if (env.type === 'snapshot') {
				const isDepthSnapshot =
					typeof env.ch === 'string' && env.ch === ch.depthCh;
				if (isDepthSnapshot || env.bids != null || env.asks != null) {
					bidMap.current.clear();
					askMap.current.clear();
					mergeSide(bidMap.current, parseLevels(env.bids ?? []));
					mergeSide(askMap.current, parseLevels(env.asks ?? []));
					emitDepth();
				}
				if (env.klines?.length && env.ch === ch.klineCh) {
					const parsed = env.klines
						.map(parseKline)
						.filter((c): c is KlineCandle => c != null);
					if (parsed.length) {
						setKlines(fillKlineGaps(parsed, klineIntervalRef.current));
					}
				}
				if (env.ch === ch.ordersCh) {
					const open = (env.open_orders ?? [])
						.map(pushOrderToRow)
						.filter((r): r is PushHubOrderRow => r != null);
					const hist = (env.history_orders ?? [])
						.map(pushOrderToRow)
						.filter((r): r is PushHubOrderRow => r != null);
					openOrdersRef.current = open;
					historyOrdersRef.current = hist;
					setOpenOrders(open);
					setHistoryOrders(hist);
				}
				return;
			}
			if (env.type === 'delta' && env.ch === ch.depthCh) {
				mergeSide(bidMap.current, parseLevels(env.b));
				mergeSide(askMap.current, parseLevels(env.a));
				emitDepth();
				return;
			}
			if (env.type === 'trade' && env.ch === ch.tradeCh && env.trade) {
				const row = pushTradeToClobRow(env.trade);
				if (row) {
					setTrades((prev) => mergeTrades(prev, [row], tradeLimitRef.current));
					onTradeRef.current?.();
				}
				return;
			}
			if (env.type === 'kline' && env.ch === ch.klineCh && env.kline) {
				const candle = parseKline(env.kline);
				if (candle) setKlines((prev) => mergeKline(prev, candle));
				return;
			}
			if (env.type === 'order' && env.order) {
				const row = pushOrderToRow(env.order);
				if (row) {
					const next = applyPushedOrder(openOrdersRef.current, historyOrdersRef.current, row);
					openOrdersRef.current = next.open;
					historyOrdersRef.current = next.history;
					setOpenOrders(next.open);
					setHistoryOrders(next.history);
				}
			}
		};

		const connect = () => {
			if (closed) return;
			try {
				ws = new WebSocket(wsUrl);
				wsRef.current = ws;
			} catch (e) {
				setConnected(false);
				setError(e instanceof Error ? e.message : String(e));
				reconnectTimer = setTimeout(connect, 3000);
				return;
			}
			ws.onopen = () => {
				setConnected(true);
				setError(undefined);
				subscribe();
			};
			ws.onmessage = (ev) => {
				try {
					handle(JSON.parse(String(ev.data)) as PushEnvelope);
				} catch {
					/* ignore */
				}
			};
			ws.onerror = () => {
				if (!closed) {
					setConnected(false);
					setError('WebSocket error');
				}
			};
			ws.onclose = () => {
				wsRef.current = null;
				if (closed) return;
				setConnected(false);
				reconnectTimer = setTimeout(connect, 3000);
			};
		};

		connect();

		return () => {
			closed = true;
			if (reconnectTimer) clearTimeout(reconnectTimer);
			try {
				ws?.close();
			} catch {
				/* ignore */
			}
			wsRef.current = null;
		};
	}, [wsUrl, enabled]);

	// Resubscribe when channels (pair / interval) change — keep socket open.
	useEffect(() => {
		if (!enabled || !channels) return;
		bidMap.current.clear();
		askMap.current.clear();
		setBids([]);
		setAsks([]);
		const ws = wsRef.current;
		if (ws && ws.readyState === WebSocket.OPEN) {
			const chans = [channels.depthCh, channels.tradeCh, channels.klineCh];
			if (owner) chans.push(channels.ordersCh);
			ws.send(
				JSON.stringify({
					op: 'subscribe',
					ch: chans,
					depth: depthLimit,
					...(owner ? { owner } : {}),
				}),
			);
		}
	}, [enabled, channels, depthLimit, owner]);

	return { connected, bids, asks, klines, trades, openOrders, historyOrders, error };
}
